<template>
  <div>
    <legend-panel/>
    <graph-panel/>
    <gmap-map
      class="map-container"
      ref="mapRef"
      :center="boundsLatLng.center"
      :options="gmapOptions"
      :zoom="5"
    >
      <gmap-marker
        v-if="this.selectedZip"
        :clickable="true"
        :position="this.selectedZipLatLng"
        @click="clickZipMarker"
      ></gmap-marker>
      <!-- <gmap-cluster> -->
        <gmap-marker
          v-for="(m, index) in markers"
          :icon="markerIcon(m, index)"
          :key="index"
          :clickable="true"
          :position="m.latLng"
          :visible="markersVisible"
          @click="clickMarker(m.tx_ctr_id || m.name)"
          @mouseover="hoverMarker(m.tx_ctr_id || m.name)"
          @mouseout="unhoverMarker(m.tx_ctr_id || m.name)"
        ></gmap-marker>
      <!-- </gmap-cluster> -->
      <map-data-layer></map-data-layer>
    </gmap-map>
  </div>
</template>

<script>
/* global google */

import HoverPanel from './HoverPanel'
import HoverWindow from './HoverWindow'
import LegendPanel from './Dialogs/LegendPanel'
import MapDataLayer from './MapDataLayer'
import CustomPopup from '../classes/Popup'
import QueryPanel from './Toolboxes/QueryPanel'
import GraphPanel from './Dialogs/GraphPanel'
import { mapGetters } from 'vuex'
// import { scaleLinear } from 'd3-scale' // For SVG markers
import { styles } from '../classes/GoogleMapStyles'
import statFun from '../api/txc'

export default {
  name: 'MapPanel',

  components: {
    HoverWindow,
    HoverPanel,
    LegendPanel,
    MapDataLayer,
    QueryPanel,
    GraphPanel
  },

  data () {
    return {
      popup: null,

      google: null,

      gmapOptions: {
        fullscreenControl: false,
        mapTypeControl: false,
        minZoom: 4,
        streetViewControl: false,
        styles: styles
      },

      bounds: undefined,

      boundsLatLng: {
        northeast: { lat: 49.5, lng: -67.0 },
        southwest: { lat: 25.2, lng: -124.7 },
        center: { lat: 39.8333333, lng: -98.585522 }
      }
    }
  },

  computed: {
    ...mapGetters({
      hospitals: 'hospitals',
      preferredTxCenter: 'preferredTxCenter',
      markersVisible: 'markersVisible',
      markersSource: 'markersSource',
      queryOrganMode: 'queryOrganMode',
      queryRegionMode: 'queryRegionMode',
      queryYear: 'queryYear',
      selectedTxCenter: 'selectedTxCenter',
      selectedStatistic: 'selectedStatistic',
      selectedZip: 'selectedZip',
      selectedZipClosestTxCenters: 'selectedZipClosestTxCenters',
      selectedZipLatLng: 'selectedZipLatLng',
      txCenters: 'txCenters',
      txRecords: 'txRecords'
    }),

    markers: function () {
      switch (this.markersSource) {
        case 'txCenters':
          return this.txCenters

        case 'hospitals':
          return this.hospitals

        default:
          return []
      }
    },

    selectedZipClosestTxCentersIds: function () {
      return this.selectedZipClosestTxCenters.map(function (txCenter) { return txCenter.tx_ctr_id })
    }
  },

  mounted () {
    var self = this
    this.$refs.mapRef.$mapPromise.then((map) => {
      // Implement behaviour to restrain to bounds
      self.bounds = new google.maps.LatLngBounds(
        self.boundsLatLng.southwest,
        self.boundsLatLng.northeast
      )
      map.panToBounds(self.bounds, -100)
      google.maps.event.addListener(map, 'dragend', function () { self.constrainBounds(map) })

      // Instantiate custom popup
      if (!this.popup) {
        let Popup = CustomPopup.doClass()
        this.popup = new Popup()
        this.popup.setMap(map)
      }

      // Save google object
      this.$store.commit('setGoogle', google)
    })
  },

  methods: {
    clickMarker: function (id) {
      this.$store.commit('selectRegion', null)

      switch (this.markersSource) {
        case 'txCenters':
          this.$store.commit('selectHospital', null)
          this.$store.commit('selectTxCenter', id)
          break

        case 'hospitals':
          this.$store.commit('selectHospital', id)
          this.$store.commit('selectTxCenter', null)
          break
      }
    },

    clickZipMarker: function () { },

    hoverMarker: function (id) {
      switch (this.markersSource) {
        case 'txCenters':
          for (let i = 0; i < this.markers.length; i++) {
            if (this.markers[i].tx_ctr_id === id) {
              // TODO: Should be refactored
              let query = {
                organMode: this.queryOrganMode,
                regionMode: this.queryRegionMode,
                year: this.queryYear
              }
              let data = statFun['tx_stat_' + this.selectedStatistic.value](this.txRecords, query, id)
              let yearIndex = data.years.indexOf(this.queryYear)
              let statLabel = this.selectedStatistic.label + ' (' + this.queryYear + ')'
              let statValue = isNaN(data.hist[yearIndex]) ? 'n/a' : Math.round(100 * data.hist[yearIndex]) + '%'
              // END -- Should be refactored

              this.popup.show('marker-' + i, this.markers[i].latLng, this.markers[i].name, statLabel, statValue)

              // TODO: Not used at moment
              this.$store.commit('hoverMarker', this.markers[i])
            }
          }
          break

        case 'hospitals':
          break
      }
    },

    unhoverMarker: function (id) {
      this.popup.hide()
    },

    markerIcon: function (m, index) {
      // Marker default values
      // ---------------------
      // let colorScale = scaleLinear()
      //   .domain([1, 5])
      //   .range(['yellow', 'red'])
      // fillColor = colorScale(1)

      // Circle
      let path = 'M-24,0a24,24 0 1,0 48,0a24,24 0 1,0 -48,0'
      let fillColor = '#25826a'
      let scale = 0.175
      let strokeColor = '#ffffff'
      let strokeWeight = 1

      if (this.markersSource === 'txCenters') {
        // Selected TxCenter being shown in GraphPanel
        if (m.tx_ctr_id === this.selectedTxCenter) {
          // fillColor = '#ffffff'
          // strokeColor = '#25826a'
          scale = 0.25
          strokeWeight = 2.5
        }
        // User inserted his ZIP Code
        if (this.selectedZip > 0) {
          if (this.selectedZipClosestTxCentersIds.indexOf(m.tx_ctr_id) < 0) {
            fillColor = '#aaa'
          }
        }
      } else {
        // Rounded square
        // path = 'M24-8c0 4.4-3.6 8-8 8h-32c-4.4 0-8-3.6-8-8v-32c0-4.4 3.6-8 8-8h32c4.4 0 8 3.6 8 8v32z'
        fillColor = '#25826a'
      }

      return {
        path: path,
        fillColor: fillColor,
        fillOpacity: 1,
        scale: scale,
        strokeColor: strokeColor,
        strokeWeight: strokeWeight
      }
    },

    markerIconRaster: function (m) {
      if (this.markersSource === 'txCenters') {
        // Selected TxCenter being shown in GraphPanel
        if (m.tx_ctr_id === this.selectedTxCenter) {
          // TODO
        }
      } else {
        // TODO
      }
      let highlight = this.selectedZip
      highlight = highlight && (this.selectedZipClosestTxCentersIds.indexOf(m.tx_ctr_id) >= 0)
      return highlight
        ? '/static/marker-light.png'
        : '/static/marker-dark.png'
    },

    showPopup: function (lat, lng, content) {

    },

    constrainBounds: function (map) {
      let mapCenter = map.getCenter()
      let usaCenter = this.boundsLatLng.center
      if (!this.bounds.contains(mapCenter)) {
        if (mapCenter.lat() >= usaCenter.lat) {
          if (mapCenter.lng() >= usaCenter.lng) {
            map.panTo(this.boundsLatLng.northeast)
          } else {
            map.panTo({ lat: this.boundsLatLng.northeast.lat, lng: this.boundsLatLng.southwest.lng })
          }
        } else {
          if (mapCenter.lng() >= usaCenter.lng) {
            map.panTo({ lat: this.boundsLatLng.southwest.lat, lng: this.boundsLatLng.northeast.lng })
          } else {
            map.panTo(this.boundsLatLng.southwest)
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.map-container {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.popup-tip-anchor {
  position: absolute;
}
</style>



// WEBPACK FOOTER //
// src/components/MapPanel.vue